import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { useTexts } from "../hooks/useTexts";
import "./Auth.css";

export default function Register() {
    const { getText } = useTexts();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setError(getText("register_error_mismatch", "Passwörter stimmen nicht überein"));
            return;
        }

        if (password.length < 6) {
            setError(getText("register_error_length", "Passwort muss mindestens 6 Zeichen lang sein"));
            return;
        }

        setLoading(true);
        setError("");

        try {
            await createUserWithEmailAndPassword(auth, email, password);
            navigate("/courses");
        } catch (error: any) {
            if (error.code === "auth/email-already-in-use") {
                setError(getText("register_error_exists", "Diese E-Mail ist bereits registriert"));
            } else {
                setError(getText("register_error_general", "Registrierung fehlgeschlagen. Bitte versuche es erneut."));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page-wrapper">
            <div className="auth-page-bg"></div>
            <div className="auth-particles">
                {[...Array(4)].map((_, i) => (<div key={i} className="auth-particle"></div>))}
            </div>
            <div className="auth-page-container">
                <div className="auth-page-glass">
                    <div className="auth-page-header">
                        <div className="auth-page-logo">🚀</div>
                        <h1 className="auth-page-title">{getText("register_title", "Starte deine Reise")}</h1>
                        <p className="auth-page-subtitle">{getText("register_subtitle", "Erstelle dein Konto und entdecke KI-gestütztes Lernen")}</p>
                    </div>
                    {error && (
                        <div className="auth-page-message error">
                            <span className="message-icon">⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}
                    <form onSubmit={handleRegister} className="auth-page-form">
                        <div className="auth-input-group">
                            <div className="auth-input-wrapper">
                                <span className="auth-input-icon">📧</span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={getText("register_email_placeholder", "E-Mail Adresse")}
                                    className="auth-page-input"
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>
                        <div className="auth-input-group">
                            <div className="auth-input-wrapper">
                                <span className="auth-input-icon">🔒</span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={getText("register_password_placeholder", "Passwort (min. 6 Zeichen)")}
                                    className="auth-page-input"
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className="auth-password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? "🙈" : "👁️"}
                                </button>
                            </div>
                        </div>
                        <div className="auth-input-group">
                            <div className="auth-input-wrapper">
                                <span className="auth-input-icon">✓</span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder={getText("register_confirm_placeholder", "Passwort bestätigen")}
                                    className="auth-page-input"
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>
                        <div className="password-requirements">
                            <small>{getText("register_req_1", "Mindestens 6 Zeichen")}</small>
                            <small>{getText("register_req_2", "Groß- und Kleinbuchstaben")}</small>
                            <small>{getText("register_req_3", "Zahlen und Sonderzeichen empfohlen")}</small>
                        </div>
                        <button
                            type="submit"
                            className={`auth-page-button ${loading ? "loading" : ""}`}
                            disabled={loading || !email || !password || !confirmPassword}
                        >
                            {loading ? getText("register_loading", "Wird erstellt...") : getText("register_button", "🎯 Konto erstellen")}
                        </button>
                        <div className="auth-page-divider">{getText("register_divider", "oder mit")}</div>
                        <div className="auth-page-social">
                            <button type="button" className="auth-social-button google">
                                <span style={{ fontWeight: 'bold', fontSize: '18px' }}>G</span> Google
                            </button>
                            <button type="button" className="auth-social-button github">
                                <span>🐙</span> GitHub
                            </button>
                        </div>
                        <div className="auth-page-links">
                            {getText("register_have_account", "Bereits registriert?")}{" "}
                            <Link to="/login" className="auth-page-link">{getText("register_login_link", "Zum Login")}</Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}